import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type Unavailability,
  type UnavailabilityKind,
  type UnavailabilityStatus,
  cancelMyUnavailability,
  createMyUnavailability,
  getMyLeaveBalance,
  listMyUnavailabilities,
} from "@/api/unavailabilities";

const KINDS: UnavailabilityKind[] = ["VACATION", "TRAINING", "SICK_LEAVE", "OTHER"];

function statusColor(s: UnavailabilityStatus): "default" | "success" | "warning" | "error" {
  if (s === "APPROVED") return "success";
  if (s === "REJECTED") return "error";
  if (s === "PENDING") return "warning";
  return "default";
}

export function MyUnavailabilitiesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<UnavailabilityKind>("VACATION");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const balance = useQuery({
    queryKey: ["my-leave-balance"],
    queryFn: () => getMyLeaveBalance(),
    staleTime: 60_000,
  });

  const list = useQuery({
    queryKey: ["my-unavailabilities"],
    queryFn: listMyUnavailabilities,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createMyUnavailability({
        kind,
        date_from: dateFrom,
        date_to: dateTo,
        reason: reason || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-unavailabilities"] });
      qc.invalidateQueries({ queryKey: ["my-leave-balance"] });
      setOpen(false);
      setDateFrom("");
      setDateTo("");
      setReason("");
      setSubmitError(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (typeof detail === "object" && detail !== null && (detail as { error?: string }).error === "leave_balance_exceeded") {
        const d = detail as { allocation_days: number; already_used: number; requested: number };
        setSubmitError(
          t("unavailabilities.errorOverrun", {
            allocation: d.allocation_days,
            used: d.already_used,
            requested: d.requested,
          }),
        );
      } else if (typeof detail === "string") {
        setSubmitError(detail);
      } else {
        setSubmitError((e as Error).message);
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelMyUnavailability(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-unavailabilities"] }),
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("unavailabilities.myTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("unavailabilities.mySubtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            {t("unavailabilities.create")}
          </Button>
        </Stack>

        {balance.data && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              color={
                balance.data.remaining_days <= 0
                  ? "error"
                  : balance.data.remaining_days < 5
                  ? "warning"
                  : "success"
              }
              label={t("unavailabilities.balanceRemaining", {
                remaining: balance.data.remaining_days,
                allocation: balance.data.allocation_days,
                year: balance.data.year,
              })}
            />
            <Typography variant="caption" color="text.secondary">
              {t("unavailabilities.balanceBreakdown", {
                consumed: balance.data.consumed_days,
                pending: balance.data.pending_days,
              })}
            </Typography>
          </Stack>
        )}

        {list.isLoading && <CircularProgress size={20} />}
        {list.data && list.data.length === 0 && (
          <Alert severity="info" variant="outlined">
            {t("unavailabilities.myEmpty")}
          </Alert>
        )}

        {list.data && list.data.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("unavailabilities.kind")}</TableCell>
                <TableCell>{t("unavailabilities.from")}</TableCell>
                <TableCell>{t("unavailabilities.to")}</TableCell>
                <TableCell>{t("unavailabilities.reason")}</TableCell>
                <TableCell>{t("unavailabilities.status_col")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((r: Unavailability) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Chip size="small" label={t(`unavailabilities.kindLabel.${r.kind}`)} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.date_from}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.date_to}</TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.reason ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(r.status)} label={t(`unavailabilities.status.${r.status}`)} />
                  </TableCell>
                  <TableCell align="right">
                    {(r.status === "PENDING" || r.status === "APPROVED") && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("unavailabilities.cancelConfirm"))) {
                            cancelMutation.mutate(r.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t("unavailabilities.createTitle")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("unavailabilities.kind")}</InputLabel>
                <Select
                  label={t("unavailabilities.kind")}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as UnavailabilityKind)}
                >
                  {KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(`unavailabilities.kindLabel.${k}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label={t("unavailabilities.from")}
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label={t("unavailabilities.to")}
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={t("unavailabilities.reason")}
                size="small"
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                inputProps={{ maxLength: 1000 }}
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="contained"
              onClick={() => createMutation.mutate()}
              disabled={
                createMutation.isPending || !dateFrom || !dateTo || dateFrom > dateTo
              }
            >
              {t("unavailabilities.submit")}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
